/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Site } from '@api/model';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Button } from '@mui/material';
import { green } from '@mui/material/colors';
import { darken } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useFormik } from 'formik';
import { FormikErrors } from 'formik/dist/types';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useHistory, useLocation, useParams } from 'react-router-dom';
import AdobeAnalytics from 'src/js/components/sites/AdobeAnalytics';
import Recaptcha from 'src/js/components/sites/Recaptcha';
import { validateUrl } from 'src/js/utils/YupValidate';
import * as yup from 'yup';
import instantFetchErrorHandler from '../../api/fetch-errors-handlers/InstantFetchErrorHandler';
import GoogleService from '../../api/services/GoogleService';
import { GA4_TAG_NAME, GAS_VARIABLE_NAME, MAPS_API_KEY_NAME } from '../../constant/Google';
import { GTMContainer } from '../../models/Google';
import { SitePageParams, SiteValues } from '../../models/Sites';
import { showConfirm } from '../../redux/actions/Confirm';
import {
  configureGoogleForSite,
  getGa4WebStreams,
  getGaProfiles,
  getGaWebPropertiesByAccount,
  getGtmDataByAccount
} from '../../redux/actions/Google';
import { updateCollegeSites } from '../../redux/actions/Sites';
import { useAppDispatch, useAppSelector } from '../../redux/hooks/redux';
import { makeAppStyles } from '../../styles/makeStyles';
import { renderSelectItemsWithEmpty } from '../../utils';
import {
  getTokenString,
  isGA4property,
  renderContainerLabel,
  renderWebPropertyLabel,
  renderWebStreamLabel
} from '../../utils/Google';
import GoogleLoginButton from '../common/GoogleLoginButton';
import Loading from '../common/Loading';
import Analytics from './Analytics';
import GoogleSetup from './GoogleSetup';
import TagManager from './TagManager';
import URLs from './URLs';

const useStyles = makeAppStyles()((theme, prop, classes: any) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0
  },
  textFieldWrapper: {
    display: 'flex',
    alignItems: 'baseline'
  },
  errorMessage: {
    fontSize: '12px',
    color: '#f44336'
  },
  plusButton: {
    color: darken(green[900], 0.1)
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    [`&:not(:last-child):not(.${classes.rootExpanded})`]: {
      borderBottom: 0,
    },
    [`&.${classes.rootExpanded}:before`]: {
      opacity: 1,
      top: '-18px',
    },
    '&:before': {
      opacity: 0
    }
  },
  loading: {
    height: '25px!important',
    width: '25px!important',
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '30px',
  },
  expandIcon: {
    right: 'unset',
    left: theme.spacing(1),
    position: 'absolute'
  },
  deleteIcon: {
    fontSize: '22px',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.error.main
    },
    padding: theme.spacing(1),
    margin: `-${theme.spacing(1)} -${theme.spacing(1)} -${theme.spacing(1)} auto`
  },
  summaryContent: {
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(3),
  },
  summaryRoot: {
    '&$summaryFocused': {
      backgroundColor: 'inherit'
    }
  },
  summaryFocused: {},
  zeroMargin: {
    margin: 0
  },
  caption: {
    marginTop: theme.spacing(1)
  },
  pr: {
    paddingRight: '20px'
  },
  textFieldWrapper2: {
    display: 'flex',
    alignItems: 'baseline'
  },
  floatLabel: {
    position: 'relative',
    bottom: theme.spacing(0.5)
  },
  hasClearIcon: {
    [`&.${classes.domainsRoot} .${classes.domainsInput}`]: {
      paddingRight: 0
    }
  },
  tagsInput: {
    '& .MuiInputAdornment-positionEnd': {
      display: 'none'
    }
  },
  domainsRoot: {},
  domainsInput: {},
  rootExpanded: {}
}));

const urlsSchema = yup.object({
  key: yup.string().nullable()
    .required('Key is required')
    .max(40, 'Maximum length is 40 characters')
    .matches(/^[0-9a-z-]+$/i, 'You can only use letters, numbers and "-"'),
  name: yup.mixed().required('Name is required'),
  webSiteTemplate: yup.string().nullable().when('id', {
    is: (val) => !val,
    then: schema => schema.nullable().required('Site template is required'),
  })
});

const googleSchema = yup.object({
  gtmAccountId: yup.string().nullable().when('googleAnalyticsId', {
    is: (val) => val,
    then: schema => schema.nullable().required('Tag manager account is required to use analytics'),
  }),
  gtmContainerId: yup.string().nullable().when('gtmAccountId', {
    is: (val) => val,
    then: schema => schema.nullable().required('Tag manager container is required'),
  }),
  gaWebPropertyId: yup.string().nullable()
    .test('gaWebPropertyId test 1', 'Web property is required', (val, context) => context.parent.googleAnalyticsId ? Boolean(val) : true)
    .test('gaWebPropertyId test 2', 'Select analytics account first', (val, context) => context.parent.googleAnalyticsId ? true : !val),
  gaWebStreamId: yup.string().nullable().when('gaWebPropertyId', {
    is: (val) => isGA4property(val),
    then: schema => schema.nullable().required('Web stream is required for GA4 property'),
  }),
});

const adobeSchema = yup.object({
  adobeSettings: yup.object({
    analyticsUrl: validateUrl
  })
});


const newSite: Site = {
  id: null,
  key: '',
  name: '',
  primaryDomain: '',
  webSiteTemplate: null,
  domains: {},
  gtmContainerId: null
};

export const SitesPage = () => {
  const [isConfig, setIsConfig] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);

  const loading = useAppSelector((state) => state.loading);
  const sites = useAppSelector((state) => state.sites);
  const collegeKey = useAppSelector((state) => state.college.collegeKey);
  const billingPlan = useAppSelector((state) => state.settings.billingPlan);

  const { id, page } = useParams<SitePageParams>();
  const location = useLocation();

  const dispatch = useAppDispatch();
  const appHistory = useHistory();

  const {
    profile,
    gtmAccounts,
    gaAccounts,
    gtmContainers,
    gaWebProperties,
    gaWebProfiles,
    ga4WebStreams,
    accountsChecked,
    token,
  } = useAppSelector((state) => state.google);

  const loggedWithGoogle = Boolean(profile && token);

  const initialSite = useMemo(() => sites?.find((s) => String(s.id) === id), [id, sites]);

  const currentSchema = useMemo(() => {
    switch (page) {
      case 'urls':
        return urlsSchema;
      case 'google-tag-manager':
        return googleSchema;
      case 'adobe-analytics':
        return adobeSchema
      default:
        return null;
    }
  }, [page])


  const formik  = useFormik<SiteValues>({
    initialValues: {
      ...initialSite,
      collegeKey,
      googleMapsApiKey: '',
      gaWebPropertyId: ''
    },
    validationSchema: currentSchema,
    onSubmit: (vals) => {

      switch (page) {
        case 'adobe-analytics':
        case 'recaptcha':
        case 'urls':
          const {
            collegeKey,
            googleMapsApiKey,
            gaWebPropertyId,
            ...changedSite
          } = vals;
          dispatch(updateCollegeSites({ [changedSite.id ? 'changed' : 'created']: [changedSite] }));
          break;
      }
      if (isConfig) {
        dispatch(configureGoogleForSite(vals));
      }
    },
    validate: (vals) => {
      const errorsResult: FormikErrors<SiteValues> = {};
      if (sites.length
        && vals.key
        && sites.filter((s) => s.key === `${collegeKey}-${vals.key}`).length === 1) {
        errorsResult.key = 'Key should be unique';
      }

      return errorsResult;
    }
  });

  const {
    handleSubmit,
    setValues,
    setFieldError,
    dirty,
    handleChange,
    values,
    errors,
    setFieldValue,
    isValid,
    resetForm
  } = formik;

  const gtmContainer = useMemo<GTMContainer>(() => {
    let container: GTMContainer = null;

    if (!values.gtmContainerId || values.gtmContainerId === 'new') {
      return container;
    }

    for (const key in gtmContainers) {
      gtmContainers[key]?.forEach((con) => {
        if (con.publicId === values.gtmContainerId) {
          container = con;
        }
      });
    }
    return container;
  }, [gtmContainers, values.gtmContainerId]);

  const gaAccountItems = useMemo(() => renderSelectItemsWithEmpty({
    items: gaAccounts,
    valueKey: 'id',
    labelKey: 'name'
  }), [gaAccounts]);

  const gtmAccountItems = useMemo(() => renderSelectItemsWithEmpty(
    {
      items: gtmAccounts,
      valueKey: 'accountId',
      labelKey: 'name'
    }
  ), [gtmAccounts]);

  const gtmContainerItems = useMemo(() => renderSelectItemsWithEmpty(
    {
      items: [...((gtmContainers || {})[values.gtmAccountId] || []), { publicId: 'new', name: 'Create new container' }],
      valueKey: 'publicId',
      labelCondition: renderContainerLabel
    }
  ), [gtmContainers, values.gtmAccountId]);

  const gaWebPropertyItems = useMemo(() => renderSelectItemsWithEmpty(
    {
      items: [...((gaWebProperties || {})[values.googleAnalyticsId] || []), { id: 'new', name: 'Create new web property' }],
      valueKey: 'id',
      labelCondition: renderWebPropertyLabel
    }
  ), [gaWebProperties, values.googleAnalyticsId]);

  const ga4WebStreamItems = useMemo(() => renderSelectItemsWithEmpty(
    {
      items: [...((ga4WebStreams || {})[values.gaWebPropertyId] || []), { webStreamData: { measurementId: 'new' }, displayName: 'Create new web stream' }],
      valueKey: 'webStreamData.measurementId',
      labelCondition: renderWebStreamLabel
    }
  ), [ga4WebStreams, values.gaWebPropertyId]);

  const concurentAccounts = values.gtmContainerId
    && values.gtmContainerId !== 'new'
    && gtmContainers
    && !Object.keys(gtmContainers)
      .some((k) => gtmContainers[k]
        .some((c) => c.publicId === values.gtmContainerId));

  // Update GTM data on account change
  useEffect(() => {
    if (values.gtmAccountId && !gtmContainers[values.gtmAccountId] && loggedWithGoogle) {
      dispatch(getGtmDataByAccount(values.gtmAccountId));
    }
  }, [gtmContainers, values.gtmAccountId, loggedWithGoogle]);

  // Update GA web properties on account change
  useEffect(() => {
    if (values.googleAnalyticsId && !gaWebProperties[values.googleAnalyticsId] && loggedWithGoogle) {
      dispatch(getGaWebPropertiesByAccount(values.googleAnalyticsId));
    }
  }, [gaWebProperties, values.googleAnalyticsId, loggedWithGoogle]);

  // Update GA profiles on property change
  useEffect(() => {
    if (values.googleAnalyticsId
      && values.gaWebPropertyId
      && values.gaWebPropertyId !== 'new'
      // exclude GA4
      && !isGA4property(values.gaWebPropertyId)
      && !gaWebProfiles[values.gaWebPropertyId]
      && loggedWithGoogle
    ) {
      dispatch(getGaProfiles(values.googleAnalyticsId, values.gaWebPropertyId));
    }
  }, [gaWebProfiles, values.gaWebPropertyId, loggedWithGoogle]);

  // Update GA4 profiles on property change
  useEffect(() => {
    if (values.googleAnalyticsId
      && values.gaWebPropertyId
      && values.gaWebPropertyId !== 'new'
      && isGA4property(values.gaWebPropertyId)
      && !ga4WebStreams[values.gaWebPropertyId]
      && loggedWithGoogle
    ) {
      dispatch(getGa4WebStreams(values.gaWebPropertyId));
    }
  }, [values.gaWebPropertyId, loggedWithGoogle]);


  useEffect(() => {
    if (initialSite) {
      resetForm({
        values: {
          ...initialSite,
          collegeKey,
          googleMapsApiKey: '',
          gaWebPropertyId: '',
          googleAnalyticsId: ''
        }
      });
    }
  }, [collegeKey, initialSite]);

  const getGoogleData = async () => {
    setCustomLoading(true);
    try {
      const googleToken = getTokenString({ token } as any);

      const googleData = {
        googleMapsApiKey: '',
        gaWebPropertyId: '',
        gaWebStreamId: ''
      };

      const workspaces = await GoogleService.getGTMWorkspaces(
        googleToken,
        initialSite.gtmAccountId,
        gtmContainer.containerId
      );

      const workspace = workspaces?.workspace[0]?.workspaceId;

      if (workspace) {
        await GoogleService.getGTMPreview(
          googleToken,
          initialSite.gtmAccountId,
          gtmContainer.containerId,
          workspace
        ).then(async (res) => {
          if (res.containerVersion.variable) {
            res.containerVersion.variable.forEach((v) => {
              if (v.name === MAPS_API_KEY_NAME) {
                v.parameter.forEach((p) => {
                  if (p.key === 'value') {
                    googleData.googleMapsApiKey = p.value;
                  }
                });
              }
              if (v.name === GAS_VARIABLE_NAME) {
                v.parameter.forEach((p) => {
                  if (p.key === 'trackingId') {
                    googleData.gaWebPropertyId = p.value;
                  }
                });
              }
            });
          }
          
          const GA4Tag = res.containerVersion?.tag?.find(t => t.name === GA4_TAG_NAME);
          
          if (GA4Tag) {
            GA4Tag.parameter.forEach(p => {
              if (p.key === 'measurementId') {
                googleData.gaWebStreamId = p.value;
              }
            });

            if (googleData.gaWebStreamId && initialSite.googleAnalyticsId) {
              const propertyG4 = await GoogleService.getGAV4WebProperties(googleToken, initialSite.googleAnalyticsId);
              for (const property of propertyG4.properties) {
                const stream = await GoogleService.getGAV4WebStreams(googleToken, property.name);
                const matchedStream = stream.dataStreams?.find(s => s.webStreamData.measurementId === googleData.gaWebStreamId);
                if (matchedStream) {
                  googleData.gaWebPropertyId = matchedStream.name.replace(/\/dataStreams\/[\d]+/g, '');
                  break;
                }
              }
            }
          }
        });

        resetForm({
          values: {
            ...initialSite,
            ...googleData,
            collegeKey,
          }
        });
      }
      setCustomLoading(false);
    } catch (e) {
      setCustomLoading(false);
      instantFetchErrorHandler(dispatch, e);
    }
  };

  useEffect(() => {
    if (!isConfig && initialSite?.id && initialSite?.gtmContainerId && initialSite.gtmAccountId && gtmContainer && token?.access_token) {
      getGoogleData();
    }
  }, [initialSite?.id, gtmContainer, isConfig, token?.access_token]);


  useEffect(() => {
    if (id === 'new') {
      setValues({
        ...newSite,
        collegeKey,
        gtmAccountId: '',
        googleMapsApiKey: '',
        gaWebPropertyId: '',
        googleAnalyticsId: ''
      });
    }
  }, [id]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const openSettings = search.get('openSettings');
    if (openSettings) {
      window.history.replaceState(null, null, window.location.pathname);
      setIsConfig(true);
    } else {
      setIsConfig(false);
    }
  }, [id, page, location]);

  const { classes } = useStyles();

  const onClickDelete = () => {
    if (values.id) {
      dispatch(showConfirm({
        confirmButtonText: 'Delete',
        confirmMessage: 'Site will be removed with all settings and configuration. This action can not be undone',
        onConfirm: () => {
          dispatch(updateCollegeSites({ removed: [values.id] }));
          appHistory.push(`/websites/${sites[0]?.id}/urls`);
        }
      }));
    }
  };

  const notLoggedWarning = (
    <Alert severity="warning">
      <AlertTitle>Not logged into Google</AlertTitle>
      Please login above to set up Google services and see website analytics
    </Alert>
  );

  const hasNoGAAccounts = !(customLoading || loading) && gaAccountItems.length === 1;
  const hasNoGTMAccounts = !(customLoading || loading) && gtmAccountItems.length === 1;

  const renderPage = () => {
    if (isConfig) {
      return loggedWithGoogle ? (
        <GoogleSetup
          loading={loading}
          classes={classes}
          site={values}
          error={errors as any}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          gaAccountItems={gaAccountItems}
          gtmAccountItems={gtmAccountItems}
          gaWebPropertyItems={gaWebPropertyItems}
          ga4WebStreamItems={ga4WebStreamItems}
          gtmContainerItems={gtmContainerItems}
          concurentAccounts={concurentAccounts}
        />
      ) : (
        notLoggedWarning
      );
    }

    switch (page) {
      case 'recaptcha':
        return <Recaptcha formik={formik} />
      case 'adobe-analytics':
        return <AdobeAnalytics formik={formik} />
      case 'urls':
        return (
          <URLs
            classes={classes}
            isNew={!values.id}
            collegeKey={collegeKey}
            site={values}
            initial={initialSite}
            error={errors as any}
            setFieldValue={setFieldValue}
            setFieldError={setFieldError}
            handleChange={handleChange}
            billingPlan={billingPlan}
          />
        );

      case 'google-tag-manager':
        return loggedWithGoogle ? (
          <TagManager
            accountsChecked={accountsChecked}
            hasNoGTMAccounts={hasNoGTMAccounts}
            gtmContainerId={values.gtmContainerId}
            gtmContainer={gtmContainer}
            googleProfileEmail={profile?.email}
            dispatch={dispatch}
          />
        ) : notLoggedWarning;
      case 'google-analytics':
        return loggedWithGoogle ? (
          <Analytics
            accountsChecked={accountsChecked}
            key={values.gaWebPropertyId}
            gaWebPropertyId={values.gaWebPropertyId}
            googleAnalyticsId={values.googleAnalyticsId}
            googleProfileEmail={profile?.email}
            hasNoGAAccounts={hasNoGAAccounts}
            dispatch={dispatch}
          />
        ) : notLoggedWarning;
      default:
        return null;
    }
  };


  const actionButton =
    isConfig || ['recaptcha', 'urls', 'adobe-analytics'].includes(page)
      ? {
        children: 'Save',
        type: 'submit',
        loading: customLoading || loading,
        disabled: !isValid || !dirty,
      }
      : !isConfig && page === 'google-analytics'
        ? {
          children: 'Open analytics',
          onClick: () =>
            window.open('https://analytics.google.com/analytics/web/#/', 'blank'),
          className: 'mr-2',
          endIcon: <OpenInNewIcon />,
        }
        : !isConfig && page === 'google-tag-manager'
          ? {
            children: 'Open tag manager',
            onClick: () =>
              window.open(
                gtmContainer?.tagManagerUrl ||
                'https://tagmanager.google.com/#/home',
                'blank'
              ),
            className: 'mr-2',
            endIcon: <OpenInNewIcon />,
          }
          : loggedWithGoogle && !isConfig && page !== 'urls'
            ? {
              children: 'Configure',
              onClick: () => setIsConfig(true),
              loading: customLoading || loading,
              endIcon: <SettingsIcon />,
            }
            : null;


  return (
    <div className={classes.container}>

      <Tabs value={page} >
        <Tab label="Domain setup" value="urls" to={`/websites/${id}/urls`} component={NavLink} exact strict />
        <Tab label="Google tag manager" value="google-tag-manager" to={`/websites/${id}/google-tag-manager`} component={NavLink} exact strict />
        <Tab label="Google analytics" value="google-analytics" to={`/websites/${id}/google-analytics`} component={NavLink} exact strict />
        <Tab label="Adobe analytics" value="adobe-analytics" to={`/websites/${id}/adobe-analytics`} component={NavLink} exact strict />
        <Tab label="reCAPTCHA" value="recaptcha" to={`/websites/${id}/recaptcha`} component={NavLink} exact strict />
      </Tabs>

      {sites
        ? (
          <form
            className="p-6"
            onSubmit={handleSubmit}
          >
            <div>
              {['google-tag-manager', 'google-analytics'].includes(page) && <div className="d-flex justify-content-end mb-3">
                <GoogleLoginButton />
              </div>}
              <div>
                {values.hasOwnProperty('key') && renderPage()}
              </div>
              <div className={classes.buttonsWrapper}>
                {isConfig && (
                  <>
                    <Button
                      className="text-secondary"
                      onClick={() => setIsConfig(false)}
                      disabled={loading}
                      startIcon={<ChevronLeftIcon fontSize="medium" />}
                    >
                      Back
                    </Button>
                    <div className="flex-1" />
                  </>
                )}

                {page === 'urls' && (
                  <Button
                    onClick={onClickDelete}
                    disableElevation
                    color="error"
                    variant="contained"
                    className="mr-2"
                  >
                    Delete
                  </Button>
                )}

                {actionButton && <LoadingButton
                  variant="contained"
                  color="primary"
                  disableElevation
                  {...actionButton}
                  href=""
                />}
              </div>
            </div>
          </form>
        )
        : <Loading />}
    </div>
  );
};
